import React from 'react';
import { useSelector } from 'react-redux';

import { ReactComponent as EditIcon } from 'images/edit-icon.svg';
import { getLoanGoal } from 'selectors/getLoanGoal';
import { getApplicationData } from 'selectors/getApplicationData';
import { RoutePath } from 'enums/Routes';
import { CurrentFlow } from 'enums/CurrentFlow';
import { useNavigate } from 'hooks/useNavigate';
import useDispatchWithUnwrap from 'hooks/useDispatchWithUnwrap';
import { updateApplicationCurrentFlow } from 'thunks';
import { FROM_FINANCIAL_DASHBOARD_STATE } from 'utils/navigationStates';
import { hasSelectedAICoachGoals } from 'utils/aiCoachUtils';
import { DebtSummary } from 'handlers/applicationData';
import { normalizeDebtSummary } from 'components/FinancialCheckup/YourFinances/yourFinancesUtils';
import ActionCard, { ActionCardVariant } from 'components/Common/Cards/ActionCard/ActionCard';
import { AI_COACH_CHANGE_GOAL_EVENT } from 'components/FinancialCheckup/FinancialDashboard/GoalsSection';

import { getSelectedAICoachGoal, getYourGoalDisplayName, getYourGoalTrajectoryMetrics } from './getYourGoalTrajectory';

import styles from './YourGoalSection.module.scss';

const YourGoalSection: React.FC = () => {
  const navigate = useNavigate();
  const dispatchWithUnwrap = useDispatchWithUnwrap();
  const { aiCoachGoals } = useSelector(getLoanGoal);
  const { application } = useSelector(getApplicationData);

  const selectedGoal = getSelectedAICoachGoal(aiCoachGoals);

  if (!hasSelectedAICoachGoals(aiCoachGoals) || !selectedGoal) return null;

  const rawDebtSummary = application?.debtSummary as DebtSummary | undefined;
  const debtSummary = rawDebtSummary ? normalizeDebtSummary(rawDebtSummary) : undefined;
  const trajectoryMetrics = getYourGoalTrajectoryMetrics(selectedGoal, debtSummary);

  const handleChangeGoal = async () => {
    if (application && application.currentFlow !== CurrentFlow.AICoach) {
      await dispatchWithUnwrap(
        updateApplicationCurrentFlow({
          applicationId: application.id,
          currentFlow: CurrentFlow.AICoach,
        }),
      );
    }

    analytics.track(AI_COACH_CHANGE_GOAL_EVENT);
    navigate(RoutePath.AICoachGoals, FROM_FINANCIAL_DASHBOARD_STATE);
  };

  return (
    <ActionCard variant={ActionCardVariant.White}>
      <div>
        <p className={styles.title}>Your Goal</p>

        <div className={styles.titleRow}>
          <p className={styles.subtitle}>{getYourGoalDisplayName(selectedGoal)}</p>
          <button type="button" className={styles.editButton} onClick={handleChangeGoal} aria-label="Change goal">
            <EditIcon className={styles.editIcon} />
          </button>
        </div>
      </div>
      {trajectoryMetrics && (
        <div className={styles.metricsRow}>
          {trajectoryMetrics.map((metric) => (
            <div key={metric.label} className={styles.metricCard}>
              <p className={styles.metricLabel}>{metric.label}</p>
              <p className={styles.metricValue}>{metric.value}</p>
            </div>
          ))}
        </div>
      )}
    </ActionCard>
  );
};

export default YourGoalSection;
